import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Send, MessageCircle, Mail, Phone, Calendar } from "lucide-react";
import { RevealTextOnView } from "../components/RevealText";
import RevealText from "../components/RevealText";
import { API_URL, BRAND } from "../lib/brand";

const CALENDLY_URL = "https://calendly.com/teresavilardoconsulente/call_conoscitiva";

export default function Prenota() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "", message: "", preferred_date: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
    const link = document.createElement("link");
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => {
      script.remove();
      link.remove();
    };
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/booking`, form);
      toast.success("Richiesta inviata! Ti risponderò al più presto.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", topic: "", message: "", preferred_date: "" });
    } catch (err) {
      toast.error("Errore nell'invio. Riprova o scrivimi su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 md:pt-40 pb-16 px-6 md:px-10" data-testid="prenota-page">
      <div className="max-w-[1400px] mx-auto">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-6">— Prenota una consulenza</p>
        <h1 className="font-display text-[14vw] md:text-[9vw] lg:text-[10rem] leading-[0.85] text-[#3D2140] mb-8">
          <RevealText text={["Facciamo", "il primo passo."]} />
        </h1>

        {/* HERO PHOTO */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center mt-4 mb-8">
          <div className="md:col-span-7">
            <p className="text-lg md:text-xl text-[#3D2140]/75 leading-relaxed font-editorial italic max-w-2xl">
              Un momento per raccontarti — al telefono, in videocall o dal vivo. Ti ascolto, ti guardo negli occhi, cerchiamo insieme la via che sta bene alla tua famiglia.
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end">
            <img
              src={BRAND.photos.portrait}
              alt="Teresa Vilardo"
              className="w-full max-w-xs md:max-w-sm aspect-[4/5] object-cover rounded-[36px] shadow-2xl border-4 border-white"
              data-testid="prenota-hero-photo"
            />
          </div>
        </div>

        {/* CALENDLY EMBED */}
        <section className="mt-12 mb-20" data-testid="calendly-section">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-8">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-11 w-11 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center">
                  <Calendar size={18} />
                </span>
                <p className="text-xs uppercase tracking-[0.35em] text-[#FF2768] font-semibold">Call conoscitiva gratuita</p>
              </div>
              <h2 className="font-display text-5xl md:text-6xl text-[#3D2140] leading-[0.9]">
                Scegli un<br/><span className="text-[#FF2768]">momento</span> per<br/>parlarne insieme.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-lg text-[#3D2140]/75 leading-relaxed font-editorial italic">
                Una prima chiamata per conoscerci, capire i tuoi bisogni e vedere insieme il percorso più adatto. Nessun impegno, solo un vero primo contatto.
              </p>
            </div>
          </div>
          <div
            className="calendly-inline-widget rounded-[24px] overflow-hidden border border-[#81C3C9]/40 bg-white"
            data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=FF2768`}
            style={{ minWidth: "320px", height: "720px" }}
            data-testid="calendly-widget"
          />
          <div className="mt-4 text-center">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-[#3D2140]/60 hover:text-[#FF2768] link-underline" data-testid="calendly-open-external">
              Apri Calendly in una nuova finestra →
            </a>
          </div>
        </section>

        <div className="my-16 flex items-center gap-4">
          <div className="h-px flex-1 bg-[#81C3C9]/40" />
          <span className="text-[10px] uppercase tracking-[0.4em] text-[#3D2140]/50 font-semibold">Oppure scrivimi</span>
          <div className="h-px flex-1 bg-[#81C3C9]/40" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <aside className="md:col-span-4">
            <p className="text-lg text-[#3D2140]/75 leading-relaxed font-editorial text-2xl mb-10">
              Preferisci un messaggio scritto? Compila il form: ti risponderò personalmente entro 24 ore.
            </p>
            <div className="space-y-5">
              <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="prenota-whatsapp-link" className="flex items-start gap-4 group">
                <span className="h-11 w-11 rounded-full bg-[#25D366] text-white flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><MessageCircle size={18} /></span>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#81C3C9]">WhatsApp</p>
                  <p className="font-semibold text-[#3D2140]">{BRAND.whatsapp}</p>
                </div>
              </a>
              <a href={`mailto:${BRAND.email}`} data-testid="prenota-email-link" className="flex items-start gap-4 group">
                <span className="h-11 w-11 rounded-full bg-[#FF2768] text-white flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"><Mail size={18} /></span>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#81C3C9]">Email</p>
                  <p className="font-semibold text-[#3D2140] break-all">{BRAND.email}</p>
                </div>
              </a>
            </div>
          </aside>

          <div className="md:col-span-8">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white border-2 border-[#81C3C9] rounded-[32px] p-10 text-center"
                data-testid="prenota-success"
              >
                <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#FF2768] text-white mb-6"><Check size={28} /></span>
                <h2 className="font-display text-5xl text-[#3D2140] mb-3">Grazie!</h2>
                <p className="text-[#3D2140]/70 mb-6">Ho ricevuto la tua richiesta. Ti risponderò personalmente entro 24 ore.</p>
                <button onClick={() => setDone(false)} className="btn-tv-outline" data-testid="prenota-new-request">
                  Nuova richiesta
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="booking-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Nome e cognome" name="name" value={form.name} onChange={handleChange} required testId="input-name" />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required testId="input-email" />
                  <Field label="Telefono" name="phone" value={form.phone} onChange={handleChange} required testId="input-phone" />
                  <Field label="Data preferita" name="preferred_date" type="date" value={form.preferred_date} onChange={handleChange} testId="input-date" />
                </div>
                <Field label="Argomento" name="topic" value={form.topic} onChange={handleChange} required testId="input-topic" placeholder="es. Consulenza genitoriale, mediazione…" />
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-[#81C3C9] mb-2 font-semibold">Il tuo messaggio</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    data-testid="input-message"
                    placeholder="Raccontami brevemente…"
                    className="w-full border-b-2 border-[#3D2140]/20 bg-transparent py-3 focus:border-[#FF2768] transition-colors resize-none text-lg placeholder:text-[#3D2140]/30 outline-none"
                  />
                </div>
                <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <button type="submit" disabled={loading} data-testid="booking-submit" className="btn-tv-primary">
                    {loading ? "Invio in corso…" : <>Invia richiesta <Send size={16} /></>}
                  </button>
                  <p className="text-xs text-[#3D2140]/50">Riservatezza garantita. I dati non vengono condivisi.</p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required, testId, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-[#81C3C9] mb-2 font-semibold">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        placeholder={placeholder}
        className="w-full border-b-2 border-[#3D2140]/20 bg-transparent py-3 focus:border-[#FF2768] transition-colors text-lg placeholder:text-[#3D2140]/30 outline-none"
      />
    </div>
  );
}
